import type { AIModel } from '@/api/types'
import { inferAudioProductGroup } from './audio-tabs'

const DASHSCOPE_PROTOCOLS = new Set(['dashscope', 'qwen', 'bailian'])
const DASHSCOPE_PROVIDER_IDS = new Set(['dashscope', 'qwen', 'bailian'])

export type AudioFamily =
  | 'dashscope_tts'
  | 'minimax_t2a'
  | 'suno_async'
  | 'openai_speech'
  | 'volcengine_seed_audio'
  | 'elevenlabs_tts'
  | 'elevenlabs_music'
  | 'mureka_async'

const ELEVEN_DEFAULT_VOICE = 'kAIqZ7fZv234ClKXwzDx'

export function elevenLabsDefaultVoiceId(): string {
  return ELEVEN_DEFAULT_VOICE
}

export function isElevenLabsMusicModel(modelId?: string): boolean {
  const id = (modelId ?? '').toLowerCase().trim()
  if (!id || id.includes('suno')) return false
  if (id.includes('eleven') && id.includes('music')) return true
  return id === 'music_v3' || id.startsWith('music_v3') || id.endsWith('/music_v3') || id.endsWith('/eleven_music_v3')
}

export function isMurekaMusicModel(modelId?: string): boolean {
  return (modelId ?? '').toLowerCase().includes('mureka')
}

export function isElevenLabsTTSModel(modelId?: string): boolean {
  const id = (modelId ?? '').toLowerCase().trim()
  if (!id || isElevenLabsMusicModel(id)) return false
  if (id.includes('eleven_v3') || id.includes('eleven-v3') || id.includes('eleven v3')) return true
  const core = id.includes('/') ? id.slice(id.lastIndexOf('/') + 1) : id
  return core === 'eleven_v3' || core === 'eleven-v3' || core.startsWith('eleven_v3')
}

const VOLCENGINE_PROTOCOLS = new Set(['volcengine', 'volc'])
const VOLCENGINE_PROVIDER_IDS = new Set(['volcengine', 'volc'])

export function isSeedAudioModel(modelId?: string): boolean {
  const id = (modelId ?? '').toLowerCase()
  return id.includes('seed-audio') || id.includes('seed_audio') || id.includes('seed audio') || id.includes('doubao-seed-audio')
}

export function inferAudioFamily(modelId: string): AudioFamily {
  const id = modelId.toLowerCase()
  if (isElevenLabsMusicModel(id)) return 'elevenlabs_music'
  if (isMurekaMusicModel(id)) return 'mureka_async'
  if (isElevenLabsTTSModel(id)) return 'elevenlabs_tts'
  if (isSeedAudioModel(id)) return 'volcengine_seed_audio'
  if (id.includes('suno') || id.includes('music-v1') || id.startsWith('music-')) return 'suno_async'
  const mmId = id.includes('/') ? id.slice(id.lastIndexOf('/') + 1) : id
  const mmCore = mmId.replace(/^minimax[-_]/, '')
  if (
    mmCore.startsWith('speech-2') ||
    mmCore.startsWith('speech-02') ||
    mmCore.startsWith('speech-01') ||
    mmCore.startsWith('speech-0') ||
    mmCore.includes('speech-2.8') ||
    (id.includes('minimax') && id.includes('speech'))
  ) {
    return 'minimax_t2a'
  }
  if (id.includes('qwen3-tts') || id.includes('qwen-tts') || id.startsWith('cosyvoice-')) {
    return 'dashscope_tts'
  }
  return 'openai_speech'
}

function modelAudioFamily(model: AIModel): AudioFamily {
  const fromApi = model.audio_family as AudioFamily | undefined
  if (
    fromApi === 'dashscope_tts' ||
    fromApi === 'minimax_t2a' ||
    fromApi === 'suno_async' ||
    fromApi === 'openai_speech' ||
    fromApi === 'volcengine_seed_audio' ||
    fromApi === 'elevenlabs_tts' ||
    fromApi === 'elevenlabs_music' ||
    fromApi === 'mureka_async'
  ) {
    return fromApi
  }
  return inferAudioFamily(model.id)
}

function schemaHintsDashScope(model: AIModel): boolean {
  const hints = model.parameter_schema?.hints ?? []
  return hints.some((h) => h.toLowerCase().includes('dashscope'))
}

function modelProviderProtocol(model: AIModel): string {
  const family = modelAudioFamily(model)
  // 控制台 Credential 常为 custom_openai_compat，但官方 schema 已标明 dashscope TTS。
  if (family === 'dashscope_tts' && schemaHintsDashScope(model)) return 'dashscope'
  const p = (model.provider_protocol ?? '').toLowerCase().trim()
  if (p) return p
  const pid = (model.provider ?? '').toLowerCase().trim()
  if (DASHSCOPE_PROVIDER_IDS.has(pid)) return 'dashscope'
  if (VOLCENGINE_PROVIDER_IDS.has(pid)) return 'volcengine'
  if (schemaHintsDashScope(model)) return 'dashscope'
  return ''
}

const OPENAI_COMPAT_GATEWAY_PROTOCOLS = new Set([
  'custom_openai_compat',
  'openai_compat',
  'openai_direct',
  'openai_compat_modelverse',
])

/** 自定义 / OpenAI 兼容网关：运营已把 SKU 挂在该 Provider 上，画布按目录全量展示。 */
export function isOpenAICompatAudioGateway(protocol: string): boolean {
  const p = protocol.toLowerCase().trim()
  if (!p) return true
  if (DASHSCOPE_PROTOCOLS.has(p) || VOLCENGINE_PROTOCOLS.has(p)) return false
  if (p.includes('elevenlabs') || p.includes('mureka')) return false
  return OPENAI_COMPAT_GATEWAY_PROTOCOLS.has(p) || p.includes('openai_compat')
}

/** 模型家族与 Provider 协议是否匹配（避免 dashscope 上挂 MiniMax 等错配）。 */
export function isAudioModelCompatible(model: AIModel): boolean {
  const family = modelAudioFamily(model)
  const protocol = modelProviderProtocol(model)
  if (isOpenAICompatAudioGateway(protocol)) return true
  switch (family) {
    case 'dashscope_tts':
      return (
        protocol === '' ||
        DASHSCOPE_PROTOCOLS.has(protocol) ||
        schemaHintsDashScope(model)
      )
    case 'volcengine_seed_audio':
      return (
        protocol === '' ||
        VOLCENGINE_PROTOCOLS.has(protocol) ||
        (model.parameter_schema?.hints ?? []).some((h) => h.toLowerCase().includes('volcengine'))
      )
    case 'minimax_t2a':
    case 'suno_async':
    case 'elevenlabs_tts':
    case 'elevenlabs_music':
    case 'mureka_async':
      if (protocol !== '' && DASHSCOPE_PROTOCOLS.has(protocol)) return false
      return true
    case 'openai_speech':
      if (protocol !== '' && DASHSCOPE_PROTOCOLS.has(protocol)) return false
      return true
    default:
      return true
  }
}

export function filterCompatibleAudioModels(models: AIModel[]): AIModel[] {
  return models.filter(isAudioModelCompatible)
}

export function isMusicAudioModel(modelId?: string): boolean {
  if (!modelId) return false
  return inferAudioProductGroup(modelId) === 'music'
}
